import { useId } from 'react';

/*
 * Filled, coloured glyphs for the Packages, Clients and Getting live screens
 * (mockups 00.17.30, 00.24.07, 23.47.40) and the Next step screen. Drawn on a
 * 48-unit grid because lucide is outline-only. Every glyph is decorative:
 * aria-hidden, focusable="false", and always sits next to visible text.
 * Third-party marks (cloud hosting, file formats) are generic shapes, not
 * copies of their logos. Single-colour glyphs use `currentColor`.
 */

const base = (size, className, viewBox = '0 0 48 48') => ({
  width: size,
  height: size,
  viewBox,
  className,
  'aria-hidden': true,
  focusable: 'false',
});

/* ---------- Packages: plan "includes" rows and the WhatsApp chips ---------- */

/* A landscape screen with a short side bar (the patient web app). */
export function WebAppGlyph({ size = 22, className }) {
  return (
    <svg {...base(size, className)}>
      <g fill="none" stroke="currentColor" strokeWidth="3.4" strokeLinejoin="round">
        <rect x="6" y="10" width="36" height="28" rx="5" />
        <path d="M33 19v10" strokeLinecap="round" />
      </g>
    </svg>
  );
}

/* A portrait card with an exclamation mark (ongoing access to past reports). */
export function AccessGlyph({ size = 22, className }) {
  return (
    <svg {...base(size, className)}>
      <rect x="11" y="5" width="26" height="38" rx="5" fill="none" stroke="currentColor" strokeWidth="3.4" />
      <path d="M24 14v12" stroke="currentColor" strokeWidth="3.6" strokeLinecap="round" />
      <circle cx="24" cy="33" r="2.4" fill="currentColor" />
    </svg>
  );
}

/* Three people, filled, the front one larger. */
export function PeopleGlyph({ size = 22, className }) {
  return (
    <svg {...base(size, className)}>
      <g fill="currentColor">
        <circle cx="24" cy="14" r="7" />
        <circle cx="10.5" cy="18" r="5.2" opacity="0.8" />
        <circle cx="37.5" cy="18" r="5.2" opacity="0.8" />
        <path d="M11 41c0-8 5.8-14 13-14s13 6 13 14Z" />
        <path d="M2 38c0-6 3.8-10.5 8.5-10.5 2 0 3.8.7 5.2 2A17 17 0 0 0 9 38Z" opacity="0.8" />
        <path d="M46 38c0-6-3.8-10.5-8.5-10.5-2 0-3.8.7-5.2 2A17 17 0 0 1 39 38Z" opacity="0.8" />
      </g>
    </svg>
  );
}

/* A filled cog with a round hole. */
export function CogGlyph({ size = 22, className }) {
  const teeth = [0, 45, 90, 135, 180, 225, 270, 315];
  return (
    <svg {...base(size, className)}>
      <g fill="currentColor">
        {teeth.map((deg) => (
          <rect key={deg} x="20" y="3" width="8" height="10" rx="2" transform={`rotate(${deg} 24 24)`} />
        ))}
        <circle cx="24" cy="24" r="15" />
      </g>
      <circle cx="24" cy="24" r="6" fill="#fff" />
    </svg>
  );
}

/* A filled calendar with a grid of white days. */
export function CalendarGlyph({ size = 22, className }) {
  return (
    <svg {...base(size, className)}>
      <rect x="5" y="9" width="38" height="34" rx="6" fill="currentColor" />
      <rect x="9" y="19" width="30" height="20" rx="2.5" fill="#fff" />
      <g fill="currentColor">
        {[0, 1, 2, 3].map((c) =>
          [0, 1].map((r) => <rect key={`${c}-${r}`} x={12 + c * 7} y={22 + r * 8} width="4.4" height="4.4" rx="1" />),
        )}
      </g>
      <path d="M15 5v8M33 5v8" stroke="currentColor" strokeWidth="4" strokeLinecap="round" />
    </svg>
  );
}

/* A green speech bubble with three dots (health tips). */
export function ChatDotsGlyph({ size = 22, className }) {
  return (
    <svg {...base(size, className)}>
      <path d="M8 8h32a5 5 0 0 1 5 5v18a5 5 0 0 1-5 5H20l-9 7v-7H8a5 5 0 0 1-5-5V13a5 5 0 0 1 5-5Z" fill="currentColor" />
      <g fill="#fff">
        <circle cx="15" cy="22" r="2.8" />
        <circle cx="24" cy="22" r="2.8" />
        <circle cx="33" cy="22" r="2.8" />
      </g>
    </svg>
  );
}

/* Rising bars with an arrow over them (patient retention). */
export function GrowthBarsGlyph({ size = 22, className }) {
  return (
    <svg {...base(size, className)}>
      <g fill="currentColor">
        <rect x="5" y="31" width="7" height="12" rx="1.6" />
        <rect x="16" y="25" width="7" height="18" rx="1.6" />
        <rect x="27" y="28" width="7" height="15" rx="1.6" />
        <rect x="38" y="19" width="7" height="24" rx="1.6" />
      </g>
      <g fill="none" stroke="currentColor" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M5 22l10-9 9 6 16-13" />
        <path d="M32 6h8v8" />
      </g>
    </svg>
  );
}

/* ---------- Clients: stat tiles and testimonial avatars ---------- */

/* A filled office building with white windows. */
export function BuildingGlyph({ size = 26, className }) {
  return (
    <svg {...base(size, className)}>
      <path d="M8 43V12l18-6v37Z" fill="currentColor" />
      <path d="M26 43V17h14v26Z" fill="currentColor" opacity="0.72" />
      <g fill="#fff">
        {[0, 1, 2, 3].map((r) =>
          [0, 1].map((c) => <rect key={`${r}-${c}`} x={12 + c * 7} y={16 + r * 6.5} width="4" height="3.6" rx="0.8" />),
        )}
        <rect x="30" y="22" width="6" height="3.4" rx="0.8" />
        <rect x="30" y="29" width="6" height="3.4" rx="0.8" />
        <rect x="14" y="38" width="6" height="5" />
      </g>
      <path d="M4 43h40" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
    </svg>
  );
}

/* A columned institution building (the agilus avatar). */
export function InstitutionGlyph({ size = 26, className }) {
  return (
    <svg {...base(size, className)}>
      <path d="M24 5 6 15v4h36v-4Z" fill="currentColor" />
      <g fill="currentColor" opacity="0.85">
        <rect x="9" y="22" width="5" height="15" rx="1" />
        <rect x="18" y="22" width="5" height="15" rx="1" />
        <rect x="27" y="22" width="5" height="15" rx="1" />
        <rect x="36" y="22" width="5" height="15" rx="1" />
      </g>
      <rect x="5" y="39" width="38" height="5" rx="1.5" fill="currentColor" />
      <path d="M21 12h6M24 9v6" stroke="#fff" strokeWidth="2.4" strokeLinecap="round" />
    </svg>
  );
}

/* A filled page with a folded corner and white lines. */
export function DocGlyph({ size = 26, className }) {
  return (
    <svg {...base(size, className)}>
      <path d="M12 4h17l11 11v25a4 4 0 0 1-4 4H12a4 4 0 0 1-4-4V8a4 4 0 0 1 4-4Z" fill="currentColor" />
      <path d="M29 4v8a3 3 0 0 0 3 3h8Z" fill="#fff" opacity="0.55" />
      <path d="M15 22h18M15 29h18M15 36h12" stroke="#fff" strokeWidth="3" strokeLinecap="round" />
    </svg>
  );
}

/* A conical lab flask with liquid. */
export function FlaskGlyph({ size = 26, className }) {
  return (
    <svg {...base(size, className)}>
      <path
        d="M18 5h12M20 5v13L8 38a4 4 0 0 0 3.5 6h25a4 4 0 0 0 3.5-6L28 18V5"
        fill="none"
        stroke="currentColor"
        strokeWidth="3.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path d="M14.5 29h19l5 9a2.4 2.4 0 0 1-2.1 3.6H11.6A2.4 2.4 0 0 1 9.5 38Z" fill="currentColor" />
    </svg>
  );
}

/* A globe with meridians. */
export function GlobeGlyph({ size = 26, className }) {
  return (
    <svg {...base(size, className)}>
      <g fill="none" stroke="currentColor" strokeWidth="3.2">
        <circle cx="24" cy="24" r="18" />
        <ellipse cx="24" cy="24" rx="8" ry="18" />
        <path d="M6 24h36M9 14h30M9 34h30" />
      </g>
    </svg>
  );
}

/* ---------- Getting live: hub, format and security tiles ---------- */

/* A stacked database (LIS / HIS), filled. */
export function DatabaseGlyph({ size = 20, className }) {
  return (
    <svg {...base(size, className)}>
      <g fill="currentColor">
        <ellipse cx="24" cy="10" rx="16" ry="6" />
        <path d="M8 15c2 3.6 8.6 6 16 6s14-2.4 16-6v8c-2 3.6-8.6 6-16 6S10 26.6 8 23Z" opacity="0.85" />
        <path d="M8 28c2 3.6 8.6 6 16 6s14-2.4 16-6v9c0 3.3-7.2 6-16 6S8 40.3 8 37Z" opacity="0.7" />
      </g>
    </svg>
  );
}

/* Code brackets with a slash, as one bold glyph. */
export function CodeGlyph({ size = 20, className }) {
  return (
    <svg {...base(size, className)}>
      <g fill="none" stroke="currentColor" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round">
        <path d="M15 14 5 24l10 10M33 14l10 10-10 10M28 9l-8 30" />
      </g>
    </svg>
  );
}

/* Curly braces. */
export function BracesGlyph({ size = 20, className }) {
  return (
    <svg {...base(size, className)}>
      <g fill="none" stroke="currentColor" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17 7h-2a4 4 0 0 0-4 4v8a5 5 0 0 1-5 5 5 5 0 0 1 5 5v8a4 4 0 0 0 4 4h2" />
        <path d="M31 7h2a4 4 0 0 1 4 4v8a5 5 0 0 0 5 5 5 5 0 0 0-5 5v8a4 4 0 0 1-4 4h-2" />
      </g>
    </svg>
  );
}

/* A purple message file with a folded corner and a few lines (HL7). */
export function MessageFileGlyph({ size = 30, className }) {
  const g = `${useId()}-g`;
  return (
    <svg {...base(size, className)}>
      <defs>
        <linearGradient id={g} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#a78bfa" />
          <stop offset="1" stopColor="#7c5cfa" />
        </linearGradient>
      </defs>
      <path d="M10 4h20l10 10v28a3 3 0 0 1-3 3H10a3 3 0 0 1-3-3V7a3 3 0 0 1 3-3Z" fill={`url(#${g})`} />
      <path d="M30 4v8a2 2 0 0 0 2 2h8Z" fill="#fff" opacity="0.5" />
      <path d="M14 30h8v8M26 26v12h7" fill="none" stroke="#fff" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" opacity="0.9" />
    </svg>
  );
}

/* A two-tone flame (FHIR). */
export function FlameGlyph({ size = 30, className }) {
  const g = `${useId()}-g`;
  return (
    <svg {...base(size, className)}>
      <defs>
        <linearGradient id={g} x1="0" y1="1" x2="0" y2="0">
          <stop offset="0" stopColor="#e5361f" />
          <stop offset="1" stopColor="#ff8a1f" />
        </linearGradient>
      </defs>
      <path d="M24 3c2 8 12 12 12 25a12 12 0 0 1-24 0c0-6 3-10 6-13 0 5 2 8 5 8-2-7 0-14 1-20Z" fill={`url(#${g})`} />
      <path d="M24 26c1 4 6 6 6 11a6 6 0 0 1-12 0c0-3 2-5 3-6 0 2 1 3 2 3-.5-3 0-6 1-8Z" fill="#ffd36b" />
    </svg>
  );
}

/* A red document with a white "PDF" ribbon. */
export function PdfGlyph({ size = 30, className }) {
  return (
    <svg {...base(size, className)}>
      <path d="M11 4h18l10 10v28a3 3 0 0 1-3 3H11a3 3 0 0 1-3-3V7a3 3 0 0 1 3-3Z" fill="#e5361f" />
      <path d="M29 4v8a2 2 0 0 0 2 2h8Z" fill="#fff" opacity="0.5" />
      <text x="23.5" y="33" fill="#fff" fontFamily="Inter, Arial, sans-serif" fontSize="11.5" fontWeight="800" textAnchor="middle">
        PDF
      </text>
    </svg>
  );
}

/* A green sheet with a white grid (CSV). */
export function SheetGlyph({ size = 30, className }) {
  return (
    <svg {...base(size, className)}>
      <path d="M11 4h18l10 10v28a3 3 0 0 1-3 3H11a3 3 0 0 1-3-3V7a3 3 0 0 1 3-3Z" fill="#1fa463" />
      <path d="M29 4v8a2 2 0 0 0 2 2h8Z" fill="#fff" opacity="0.5" />
      <g fill="#fff">
        {[0, 1, 2, 3].map((r) =>
          [0, 1, 2].map((c) => <rect key={`${r}-${c}`} x={13 + c * 7.6} y={20 + r * 5.6} width="6" height="4" rx="0.6" />),
        )}
      </g>
    </svg>
  );
}

/* An orange shield badge with white angle brackets (HTML; generic, not a logo copy). */
export function MarkupShieldGlyph({ size = 30, className }) {
  return (
    <svg {...base(size, className)}>
      <path d="M8 4h32l-3 34-13 6-13-6Z" fill="#f2541b" />
      <path d="M24 7h13l-2.6 29.4L24 41Z" fill="#ff7a3d" />
      <path d="M19 17l-6 7 6 7M29 17l6 7-6 7" fill="none" stroke="#fff" strokeWidth="3.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/* An outlined shield with a tick (HIPAA). */
export function ShieldTickGlyph({ size = 34, className }) {
  return (
    <svg {...base(size, className)}>
      <path
        d="M24 4 8 10v12c0 10.5 6.8 18.6 16 22 9.2-3.4 16-11.5 16-22V10Z"
        fill="#eaf1fe"
        stroke="currentColor"
        strokeWidth="3.2"
        strokeLinejoin="round"
      />
      <path d="M24 9.5 13 13.6v8.6c0 7.6 4.6 13.6 11 16.3Z" fill="currentColor" opacity="0.14" />
      <path d="m16.5 24 5.5 5.5 10-11" fill="none" stroke="currentColor" strokeWidth="3.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/* A filled shield with a white tick (DPDP Act). */
export function ShieldSolidGlyph({ size = 34, className }) {
  const g = `${useId()}-g`;
  return (
    <svg {...base(size, className)}>
      <defs>
        <linearGradient id={g} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#2f86ff" />
          <stop offset="1" stopColor="#1453d8" />
        </linearGradient>
      </defs>
      <path d="M24 4 8 10v12c0 10.5 6.8 18.6 16 22 9.2-3.4 16-11.5 16-22V10Z" fill={`url(#${g})`} />
      <path d="m16 24 6 6 10.5-11.5" fill="none" stroke="#fff" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/* A soft filled cloud (cloud hosting; generic, not a provider logo). */
export function CloudGlyph({ size = 38, className }) {
  const g = `${useId()}-g`;
  return (
    <svg {...base(size, className)}>
      <defs>
        <linearGradient id={g} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#3b8cff" />
          <stop offset="1" stopColor="#1463f3" />
        </linearGradient>
      </defs>
      <path
        d="M14 38a10 10 0 0 1-1.6-19.9A13 13 0 0 1 37.4 16 11 11 0 0 1 36 38Z"
        fill={`url(#${g})`}
      />
    </svg>
  );
}

/* Three stacked server units with status lights. */
export function ServerGlyph({ size = 34, className }) {
  return (
    <svg {...base(size, className)}>
      {[5, 18, 31].map((y) => (
        <g key={y}>
          <rect x="8" y={y} width="32" height="11" rx="3" fill="currentColor" />
          <circle cx="14" cy={y + 5.5} r="1.8" fill="#fff" />
          <path d={`M20 ${y + 5.5}h14`} stroke="#fff" strokeWidth="2.2" strokeLinecap="round" opacity="0.8" />
        </g>
      ))}
    </svg>
  );
}

/* An outlined padlock with a keyhole (VAPT). */
export function PadlockGlyph({ size = 34, className }) {
  return (
    <svg {...base(size, className)}>
      <path d="M15 21v-6a9 9 0 0 1 18 0v6" fill="none" stroke="currentColor" strokeWidth="3.6" />
      <rect x="9" y="20" width="30" height="24" rx="5" fill="#eaf1fe" stroke="currentColor" strokeWidth="3.4" />
      <circle cx="24" cy="30" r="3" fill="currentColor" />
      <path d="M24 31v6" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
    </svg>
  );
}

/* A filled disc with a tick in a second colour (light disc, dark tick on navy bars). */
export function TickDiscGlyph({ size = 22, className, disc = '#dbe8ff', tick = '#0d2a7a' }) {
  return (
    <svg {...base(size, className)}>
      <circle cx="24" cy="24" r="20" fill={disc} />
      <path d="M15 24.5l6 6 12-13" fill="none" stroke={tick} strokeWidth="4.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/* ---------- Next step ---------- */

/* A filled phone with a white screen and a report line. */
export function PhoneGlyph({ size = 22, className }) {
  return (
    <svg {...base(size, className)}>
      <rect x="12" y="3" width="24" height="42" rx="5" fill="currentColor" />
      <rect x="15.5" y="9" width="17" height="27" rx="2" fill="#fff" />
      <path d="M19 15h10M19 20h10M19 25h6" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" />
      <circle cx="24" cy="40.5" r="1.8" fill="#fff" />
    </svg>
  );
}

/* A filled clock face. */
export function ClockGlyph({ size = 18, className }) {
  return (
    <svg {...base(size, className)}>
      <circle cx="24" cy="24" r="20" fill="currentColor" />
      <path d="M24 13v12l8 5" fill="none" stroke="#fff" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
